/**
 * Shopify Product Scraper — runs in-browser
 * Fetches products from a Shopify store's public JSON API,
 * extracts watch specs, and returns entries compatible with the BulkImport flow.
 */

import type { TaggedImage } from '@watchkeeper/shared-types';
import { stripInlineMarkup } from '../../services/stripInlineMarkup';

export interface ShopifyProduct {
  id: number;
  title: string;
  handle: string;
  body_html: string | null;
  product_type: string;
  tags: string[];
  vendor: string;
  variants: ShopifyVariant[];
  images: ShopifyImage[];
  image?: { src: string };
  options?: { name: string }[];
}

export interface ShopifyVariant {
  id: number;
  title: string;
  sku: string | null;
  price: string;
  option1: string | null;
  option2: string | null;
  option3: string | null;
  image_id: number | null;
}

export interface ShopifyImage {
  id: number;
  src: string;
  variant_ids: number[];
}

export interface ScrapedEntry {
  reference: string;
  model: string;
  collection: string;
  productName: string;
  title: string;
  description: string;
  variant: string;
  movement: string;
  movementType: string;
  jewels: string;
  caseSize: string;
  caseHeight: string;
  waterRes: string;
  caseMaterial: string;
  caseFinish: string;
  caseCoating: string;
  dialColor: string;
  hands: string;
  glass: string;
  crystal: string;
  lugWidth: string;
  lugToLug: string;
  powerReserve: string;
  dialMaterial: string;
  crownType: string;
  caseBack: string;
  strapMaterial: string;
  strapColor: string;
  imgSrc: string;
  /**
   * Every product image, the row's own image first (same shape the WooCommerce
   * and Square Online adapters emit). Set only when there is more than one —
   * the importer fills the reference's gallery from it.
   */
  taggedImages?: TaggedImage[];
  url: string;
  price: string;
  tags: string;
  /**
   * The product's Shopify `vendor`, verbatim. On a shop that sells several dial
   * brands this is the strongest signal of which one a watch carries; the
   * importer reads it to route the row (see importBrandRouting.ts). Never
   * written to the reference.
   */
  shopifyVendor: string;
  /**
   * Shopify product handle this entry's variant belongs to. Authoritative
   * product grouping key — every variant of the same Shopify product shares it.
   * The bulk importer uses it to group variants under one parent ref.
   */
  shopifyProductHandle: string;
  /**
   * Number of sibling variant entries produced from the same Shopify product.
   * `>= 2` marks a true variant product (parent + children); `1` is a
   * standalone ref imported exactly as today (no parent created).
   */
  shopifyVariantCount: number;
  /**
   * Shopify product id (numeric, as a string). Globally unique and immutable —
   * the importer uses it (with {@link shopifyVariantId}) to build a composite,
   * ref-string-independent doc id so the same SKU reused across multiple Shopify
   * products no longer collides into one doc. Authoritative product grouping key.
   */
  shopifyProductId: string;
  /**
   * Shopify variant id (numeric, as a string). Unique per variant. Forms the
   * variant/standalone doc id `shopify__{productId}__{variantId}`.
   */
  shopifyVariantId: string;
}

// ── Exclusion lists ───────────────────────────────────────────────────────────
// Accessory words: a product whose TITLE carries one is dropped by
// filterProducts — unless the title also carries a watch signal (below), or the
// curator has ticked the product back in.
//
// Matched as WHOLE WORDS (an optional plural is allowed), not substrings. As
// substrings, `cap` matched "Captain", `hat` "Chatham", `tool` "Toolwatch" and
// `polish` "Polished". Multi-word entries match across any run of punctuation
// or spaces, so `gift card` also catches "gift-card".
//
// 'bracelet' is bare (not 'bracelet only') so standalone bracelet products like
// "OKAMI TITANIUM BRACELET SILVER" are dropped.
const EXCLUDE_TITLE = ['strap', 'tool', 'gift card', 'bracelet', 'accessory', 'accessories',
  'pouch', 'patch', 'key ring', 'polish', 'cloth', 'box only', 'buckle',
  'spring bar', 'deployment', 'canvas roll', 't-shirt', 'shirt', 'hat',
  'cap', 'bag', 'backpack', 'wallet', 'crown bead'];

// Words that say "this product IS a watch", whatever else its title mentions.
// They rescue a title from EXCLUDE_TITLE: some shops sell each strap option as
// its own product and name it in the title — "… Blue Dial Silicone Strap
// Automatic 300m Men's Watch", "… Silver Dial Steel Bracelet Automatic" — and
// the accessory word alone wiped out Cestrian's entire catalogue (30 watches).
//
// "watch" is deliberately NOT a signal: "watch strap", "watch tool" and "watch
// pouch" are exactly the accessories this filter exists to drop.
const WATCH_SIGNALS = ['dial', 'automatic', 'quartz', 'chronograph', 'mechanical',
  'hand wound', 'handwound', 'hand winding', 'solar', 'kinetic'];

const escapeRegex = (text: string) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** Whole-word matcher for a keyword; words may be joined by any punctuation/space run. */
function wholeWordPattern(keyword: string, allowPlural: boolean): RegExp {
  const body = keyword.split(/[\s-]+/).map(escapeRegex).join('[^a-z0-9]*');
  return new RegExp(`(?<![a-z0-9])${body}${allowPlural ? '(?:e?s)?' : ''}(?![a-z0-9])`, 'i');
}

const EXCLUDE_TITLE_PATTERNS = EXCLUDE_TITLE.map((keyword) => ({
  keyword,
  pattern: wholeWordPattern(keyword, true),
}));
const WATCH_SIGNAL_PATTERNS = WATCH_SIGNALS.map((keyword) => wholeWordPattern(keyword, false));

/** True when the title carries a word that marks the product as a watch. */
export function hasWatchSignal(title: string): boolean {
  return WATCH_SIGNAL_PATTERNS.some((pattern) => pattern.test(title || ''));
}

/**
 * The accessory word that excludes this title, or null when the title is kept:
 * either it carries no accessory word, or a watch signal rescues it.
 */
export function titleExclusionKeyword(title: string): string | null {
  const text = title || '';
  const hit = EXCLUDE_TITLE_PATTERNS.find(({ pattern }) => pattern.test(text));
  if (!hit) return null;
  return hasWatchSignal(text) ? null : hit.keyword;
}

const EXCLUDE_TYPE = ['strap', 'straps', 'accessory', 'accessories', 'sav',
  'gift cards', 'gift card', 'option', 'retail', 'tool', 'tools'];

// Lifecycle-state words that mean "skip this product entirely". These travel
// on the Shopify HANDLE or SKU almost always — e.g. `goofy-panda-coming-soon`,
// `W-BEL-GYS-PREORDER` — and only rarely on the product title, so a
// title-substring check alone would miss most of them. We match loosely across
// title, handle, and every variant SKU: a run of non-alphanumeric characters
// (spaces, hyphens, underscores) between the words counts as the same word, so
// one entry here matches `coming-soon`, `coming soon`, `comingsoon` and
// `coming_soon` without needing a row per variant.
//
// NOTE on overlap with `DEFAULT_IMPORT_STOPWORDS` in
// `src/services/importStopwords.ts`: `coming-soon` lives in both lists on
// purpose. This list runs BEFORE grouping/import and drops the whole product;
// the stopwords list runs during ref derivation and only strips the word out
// of a SKU. If this gate catches the product, the stopword pass never sees it
// (the product is gone) so there is no double-processing. The stopword entry
// stays as belt-and-braces for anything that slips through — e.g. a product
// whose title/handle/SKU is clean but whose derived ref picks up the word from
// a tag or option name downstream.
//
// `limited edition` / `limited-edition` is DELIBERATELY omitted: it's an
// identity-bearing attribute of the watch itself, not a lifecycle state.
const EXCLUDE_LIFECYCLE = [
  'coming soon',
  'to be launched',
  'discontinued',
  'archive',
  'archived',
  'retired',
];

/**
 * True iff `haystack` contains any lifecycle keyword from `EXCLUDE_LIFECYCLE`,
 * matching loosely — the words may be joined by any run of non-alphanumeric
 * characters (so `coming-soon`, `coming soon`, `comingsoon` all match
 * `coming soon`). Boundary-gated so `archived` does not match inside
 * `archivedesign` or similar.
 *
 * Exported so the test file can pin the matcher directly.
 */
export function hasLifecycleKeyword(haystack: string): boolean {
  if (!haystack) return false;
  const low = haystack.toLowerCase();
  for (const kw of EXCLUDE_LIFECYCLE) {
    const parts = kw.split(/\s+/).map((p) => p.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const pattern = new RegExp(
      `(?<![a-z0-9])${parts.join('[^a-z0-9]*')}(?![a-z0-9])`,
      'i',
    );
    if (pattern.test(low)) return true;
  }
  return false;
}

// ── Fetch products ────────────────────────────────────────────────────────────
export async function fetchShopifyProducts(
  storeUrl: string,
  onProgress?: (msg: string) => void,
): Promise<ShopifyProduct[]> {
  const trimmed = storeUrl.replace(/\/+$/, '');
  let all: ShopifyProduct[] = [];

  // If URL already ends in .json, fetch it directly (single page, e.g. a collection or series page)
  if (trimmed.endsWith('.json')) {
    onProgress?.('Fetching JSON...');
    const resp = await fetch(trimmed);
    if (!resp.ok) throw new Error(`HTTP ${resp.status} from ${trimmed}`);
    const data = await resp.json();
    // Handle both {products: [...]} and direct array formats
    const products = data.products || (Array.isArray(data) ? data : []);
    return products;
  }

  // Standard Shopify /products.json with pagination
  let page = 1;
  while (true) {
    onProgress?.(`Fetching page ${page}...`);
    const url = `${trimmed}/products.json?limit=250&page=${page}`;
    const resp = await fetch(url);
    if (!resp.ok) throw new Error(`HTTP ${resp.status} from ${url}`);
    const data = await resp.json();
    if (!data.products?.length) break;
    all = all.concat(data.products);
    if (data.products.length < 250) break;
    page++;
  }

  return all;
}

// ── Get unique product types ──────────────────────────────────────────────────
export function getProductTypes(products: ShopifyProduct[]): { type: string; count: number }[] {
  const map = new Map<string, number>();
  products.forEach((p) => {
    const t = p.product_type || '(none)';
    map.set(t, (map.get(t) || 0) + 1);
  });
  return Array.from(map.entries())
    .map(([type, count]) => ({ type, count }))
    .sort((a, b) => b.count - a.count);
}

// ── Auto-detect which types are watches ───────────────────────────────────────
export function isLikelyWatch(type: string): boolean {
  const tLow = type.toLowerCase().trim();
  if (EXCLUDE_TYPE.some((t) => tLow === t || tLow.startsWith(t + ' -'))) return false;
  if (tLow.includes('watch') || tLow.includes('chrono') || tLow.includes('timepiece')) return true;
  if (tLow === '(none)') return false; // uncertain
  if (!EXCLUDE_TYPE.includes(tLow) && tLow.length > 0) return true; // include unknown types
  return false;
}

// ── Filter products by selected types ─────────────────────────────────────────
/**
 * Products that pass the import's first gate: a selected product type, no
 * accessory word in the title (see {@link titleExclusionKeyword}), and no
 * lifecycle word on the title, handle or SKUs.
 *
 * `includeOverrides` holds the ids of products the curator has ticked back in
 * after seeing them in the excluded-by-title list. An override lifts the TITLE
 * exclusion only — the product type must still be selected, and a lifecycle
 * word ("discontinued", "coming soon") still drops it.
 */
export function filterProducts(
  products: ShopifyProduct[],
  selectedTypes: Set<string>,
  includeOverrides?: ReadonlySet<number>,
): ShopifyProduct[] {
  return products.filter((p) => {
    const type = p.product_type || '(none)';
    if (!selectedTypes.has(type)) return false;
    // Accessory words in the title, unless the curator has overridden them.
    if (!includeOverrides?.has(p.id) && titleExclusionKeyword(p.title)) return false;
    // Lifecycle gate — check title + handle + every variant SKU, because
    // lifecycle words (`coming-soon`, `to-be-launched`, `discontinued`, …)
    // overwhelmingly live on the handle or SKU rather than the title.
    if (hasLifecycleKeyword(p.title)) return false;
    if (hasLifecycleKeyword(p.handle ?? '')) return false;
    if (p.variants?.some((v) => hasLifecycleKeyword(v.sku ?? ''))) return false;
    return true;
  });
}

/**
 * The products of the selected types that the TITLE rule excludes, each with
 * the word that did it — what the import dialog lists so a curator can see why
 * the count is lower than expected and tick any of them back in.
 *
 * Only products the title rule ALONE keeps out are listed: one that also
 * carries a lifecycle word would stay excluded even if ticked, so offering it
 * would be a checkbox that does nothing.
 */
export function titleExcludedProducts(
  products: ShopifyProduct[],
  selectedTypes: Set<string>,
): { product: ShopifyProduct; keyword: string }[] {
  const out: { product: ShopifyProduct; keyword: string }[] = [];
  for (const p of products) {
    if (!selectedTypes.has(p.product_type || '(none)')) continue;
    const keyword = titleExclusionKeyword(p.title);
    if (!keyword) continue;
    if (hasLifecycleKeyword(p.title) || hasLifecycleKeyword(p.handle ?? '')) continue;
    if (p.variants?.some((v) => hasLifecycleKeyword(v.sku ?? ''))) continue;
    out.push({ product: p, keyword });
  }
  return out;
}

// ── HTML helpers ──────────────────────────────────────────────────────────────
function strip(html: string | null): string {
  return (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:p|div|li|tr|h\d)>/gi, '\n')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>')
    .replace(/&nbsp;/g, ' ').replace(/&#39;/g, "'").replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, '\n\n').replace(/[ \t]+/g, ' ')
    .trim();
}

// ── Spec extraction (multi-format) ────────────────────────────────────────────
function extractSpecs(bodyHtml: string | null): Record<string, string | boolean> {
  const text = strip(bodyHtml);
  const textLow = text.toLowerCase();
  const specs: Record<string, string | boolean> = {};

  // Movement
  const movMatch = text.match(/\b((?:Citizen\/Miyota|Miyota|Seiko|Sellita|ETA|Ronda|SII|TMI|Soprod|Vaucher)\s*[\w\d\/.-]+)/i)
    || text.match(/\b((?:SW|NH|VK|NE|ST)\d{2,4}[\w-]*)/i);
  if (movMatch) specs.movement = movMatch[1].trim();
  if (!specs.movement) {
    const calMatch = text.match(/calibr[e]?[:\s]+([^\n›•]{3,40})/i);
    if (calMatch) specs.movement = calMatch[1].trim();
  }

  // Movement type
  if (textLow.includes('meca-quartz') || textLow.includes('mecaquartz') || /\bvk6/i.test(text)) specs.movementType = 'meca_quartz';
  else if (textLow.includes('automatic') || textLow.includes('self-winding') || /\bnh3[456789]/i.test(text) || /miyota [89]/i.test(text) || /sw[234]\d\d/i.test(text)) specs.movementType = 'automatic';
  else if (textLow.includes('manual') || textLow.includes('hand-wound') || textLow.includes('hand wound')) specs.movementType = 'manual';
  else if (textLow.includes('solar') || textLow.includes('quartz')) specs.movementType = 'quartz';

  // Case size
  const sizePatterns = [
    /(\d{2,3}(?:\.\d)?)\s*mm\s*(?:diameter|case|wide)/i,
    /(?:case|diameter)[:\s]*(\d{2,3}(?:\.\d)?)\s*mm/i,
    /in\s*a?\s*(\d{2,3})\s*mm/i,
  ];
  for (const p of sizePatterns) { const m = text.match(p); if (m) { specs.caseSize = m[1]; break; } }

  // Dimensions: "41.5 mm x 48.5 mm lug-to-lug x 11.9 mm thickness"
  const dimMatch = text.match(/(\d+\.?\d*)\s*mm\s*(?:diameter)?\s*x\s*(\d+\.?\d*)\s*mm\s*lug[- ]to[- ]lug\s*x\s*(\d+\.?\d*)\s*mm\s*thi/i);
  if (dimMatch) { specs.caseSize = specs.caseSize || dimMatch[1]; specs.lugToLug = dimMatch[2]; specs.caseHeight = dimMatch[3]; }

  // Case height
  if (!specs.caseHeight) {
    const hm = text.match(/(\d+\.?\d*)\s*mm\s*thi[cn]k/i) || text.match(/thickness[:\s]*(\d+\.?\d*)/i)
      || text.match(/(?:case\s*height|height)[:\s]*(\d+\.?\d*)\s*mm/i);
    if (hm) specs.caseHeight = hm[1];
  }

  // Lug-to-lug
  if (!specs.lugToLug) {
    const ltl = text.match(/(\d+\.?\d*)\s*mm\s*lug[- ]to[- ]lug/i)
      || text.match(/lug[- ]to[- ]lug[:\s]*(\d+\.?\d*)\s*mm/i)
      || text.match(/LUG-TO-LUG[:\s]*(\d+\.?\d*)/i);
    if (ltl) specs.lugToLug = ltl[1];
  }

  // Lug width
  const lw = text.match(/(\d+)\s*mm\s*lug\s*width/i) || text.match(/lug\s*width[:\s]*(\d+)/i)
    || text.match(/LUG WIDTH[:\s]*(\d+)/i);
  if (lw) specs.lugWidth = lw[1];

  // Water resistance — preserve the unit (ATM/bar/m) so the importer's
  // parseWaterResistance can convert correctly. Storing only the number lost the
  // signal and mis-converted high-ATM divers (e.g. "50 ATM" → 50m instead of 500m).
  const wrMatch = text.match(/(\d+)\s*(ATM|atm|bar)\b/i) || text.match(/water\s*resist[ance]*[:\s]*(\d+)\s*(m|meter)/i);
  if (wrMatch) specs.waterRes = `${wrMatch[1]} ${wrMatch[2]}`;

  // Case material
  specs.caseMaterial = 'steel';
  if (textLow.includes('titanium')) specs.caseMaterial = 'titanium';
  else if (textLow.includes('bronze')) specs.caseMaterial = 'bronze';
  else if (textLow.includes('ceramic')) specs.caseMaterial = 'ceramic';
  else if (textLow.includes('carbon')) specs.caseMaterial = 'carbon';

  // Case coating, finish
  if (textLow.includes('dlc')) specs.caseCoating = 'dlc';
  else if (textLow.includes('pvd')) specs.caseCoating = 'pvd';
  if (textLow.includes('polished') && textLow.includes('brushed')) specs.caseFinish = 'polished_brushed';
  else if (textLow.includes('brushed')) specs.caseFinish = 'brushed';
  else if (textLow.includes('polished')) specs.caseFinish = 'polished';

  // Crystal
  if (textLow.includes('sapphire')) specs.crystal = 'sapphire_crystal';
  else if (textLow.includes('mineral')) specs.crystal = 'mineral_glass';
  else if (textLow.includes('hardlex')) specs.crystal = 'hardlex';

  // Dial color
  const dialMatch = text.match(/(?:black|blue|green|white|grey|gray|silver|navy|red|orange|cream|champagne|salmon|burgundy|teal|yellow)\s*(?:dial|sunburst|sun-?ray)/i);
  if (dialMatch) specs.dialFromBody = dialMatch[0].split(/\s/)[0].toLowerCase();

  // Glass descriptive
  const glassMatch = text.match(/(?:domed |flat |double[- ]domed )?sapphire (?:glass|crystal)[^.]{0,50}/i);
  if (glassMatch) specs.glass = glassMatch[0].trim();

  // Jewels
  const jm = text.match(/(\d+)\s*(?:jewels|pcs\s*jewel)/i);
  if (jm) specs.jewels = jm[1];

  // Power reserve
  const prMatch = text.match(/power\s*reserve[:\s]*(?:approx\.?\s*)?(\d+)\s*(?:hours|hrs|h)/i);
  if (prMatch) specs.powerReserve = prMatch[1];

  // Dial material
  const dmMatch = text.match(/dial\s*material[:\s]*([^\n›•]{3,50})/i);
  if (dmMatch) specs.dialMaterial = dmMatch[1].trim();

  // Crown type
  if (textLow.includes('screw-down crown') || textLow.includes('screw down crown')) specs.crownType = 'screw_down';
  // Case back
  if (textLow.includes('screw-down caseback') || textLow.includes('screw-down case back') || textLow.includes('screw down caseback')) specs.caseBack = 'screw_down';
  else if (textLow.includes('display caseback') || textLow.includes('exhibition caseback') || textLow.includes('transparent caseback')) specs.caseBack = 'display';

  // Luminova
  if (textLow.includes('luminova') || textLow.includes('superluminova') || textLow.includes('super-luminova') || textLow.includes('lume') || textLow.includes('luminescent')) specs.hasLuminova = true;

  // Table-based specs (<td><strong>Label</strong></td><td>Value</td>)
  const tableMatches = (bodyHtml || '').matchAll(/<td[^>]*>\s*<strong>([^<]+)<\/strong>\s*<\/td>\s*<td[^>]*>([^<]+)<\/td>/gi);
  for (const m of tableMatches) {
    const label = m[1].trim().toLowerCase();
    const value = m[2].trim();
    if (label.includes('dial') && !specs.dialFromBody) specs.dialFromBody = value.toLowerCase();
    if (label.includes('hands') && !specs.hands) specs.hands = value;
    if (label.includes('diameter') && !specs.caseSize) specs.caseSize = value.replace(/[^\d.]/g, '');
    if (label.includes('case height') && !specs.caseHeight) specs.caseHeight = value.replace(/[^\d.]/g, '');
    if (label.includes('lug width') && !specs.lugWidth) specs.lugWidth = value.replace(/[^\d.]/g, '');
    if (label.includes('lug-to-lug') && !specs.lugToLug) specs.lugToLug = value.replace(/[^\d.]/g, '');
    // Keep the unit (e.g. "10 ATM", "100m") — parseWaterResistance reads it; the
    // old digit-only strip discarded the unit and mis-converted high-ATM values.
    if (label.includes('water') && !specs.waterRes) specs.waterRes = value.replace(/\s+/g, ' ').trim();
    if (label.includes('glass') && !specs.glass) specs.glass = value;
    if ((label.includes('caliber') || label.includes('calibre')) && !specs.movement) specs.movement = value;
    if (label.includes('movement') && !specs.movementType) {
      if (value.toLowerCase().includes('automatic')) specs.movementType = 'automatic';
      else if (value.toLowerCase().includes('quartz')) specs.movementType = 'quartz';
    }
  }

  // Description — keep the full text (up to 2000 chars) so specs embedded in descriptions aren't lost
  const specIdx = text.search(/SPECIFICATIONS|SPECS|Technical Details|CASE\n|Movement[:\n]/i);
  let description = specIdx > 50 ? text.substring(0, specIdx).trim() : text;
  description = description.replace(/\n/g, ' ').replace(/\s{2,}/g, ' ').trim();
  if (description.length > 2000) description = description.substring(0, 2000) + '...';
  specs.description = description;

  return specs;
}

// Canonical dial-color tokens. Used to loose-match a known color out of a fancy
// variant option value or title — e.g. "Maelstrom Blue" → "blue", "Galaxy
// Black" → "black", "Cyclone White" → "white" — so the stored value resolves
// against `lookup_dial_colors` (a raw fancy name would not). Mirrors the loose
// extraction the admin dialColor backfill tool uses.
const DIAL_COLOR_TOKENS = ['black', 'blue', 'white', 'green', 'grey', 'gray',
  'orange', 'red', 'silver', 'navy', 'teal', 'cream', 'yellow', 'burgundy',
  'salmon', 'champagne', 'midnight', 'brown', 'gold', 'bronze', 'purple',
  'pink', 'rose', 'charcoal', 'anthracite', 'ivory', 'turquoise'];

function extractDialColorToken(text: string): string {
  const low = (text || '').toLowerCase();
  for (const c of DIAL_COLOR_TOKENS) if (low.includes(c)) return c;
  return '';
}

// ── Parse products into entries ───────────────────────────────────────────────
export function parseProducts(
  products: ShopifyProduct[],
  storeUrl: string,
): ScrapedEntry[] {
  const base = storeUrl.replace(/\/+$/, '');
  const entries: ScrapedEntry[] = [];

  for (const p of products) {
    const specs = extractSpecs(p.body_html);

    // Some themes wrap parts of the title in markup the feed serves verbatim
    // (`<tc>ALLIANCE</tc> - <tc>Moon Phase</tc> - Blue`). Clean it BEFORE the
    // split, so model / collection / product name / variant are all cut from
    // the readable title rather than each inheriting a tag.
    const productTitle = stripInlineMarkup(p.title);

    // Model from title
    const titleParts = productTitle.split(/\s*[-–|]\s*/);
    const model = titleParts[0].trim();
    const titleVariant = titleParts.slice(1).join(' - ').trim();

    // Product-level dial color (fallback when a variant has no dial option).
    // Only trust a structured dial color from the body — never scan the title,
    // which produces false positives (e.g. "WHITE ART DECO" → dialColor:"white"
    // when the title color describes numerals/style, not the dial face).
    const dialColor = (specs.dialFromBody as string) || '';

    // Collection from tags
    const tags = Array.isArray(p.tags) ? p.tags : [];
    let collection = '';
    const collTag = tags.find((t) => typeof t === 'string' && t.startsWith('Collection_'));
    if (collTag) collection = stripInlineMarkup(collTag.replace('Collection_', ''));
    if (!collection) collection = model;

    // Image map
    const productImageUrls = (p.images || [])
      .map((img) => (typeof img.src === 'string' ? img.src.trim() : ''))
      .filter(Boolean);
    const imageMap: Record<number, string> = {};
    (p.images || []).forEach((img) => {
      (img.variant_ids || []).forEach((vid) => { imageMap[vid] = img.src; });
    });

    // Product-level options
    const optionDefs = (p.options || []).map((o) => (o.name || '').toLowerCase());

    // Collect this product's variant entries locally so we can stamp each with
    // the sibling variant count once the whole product is parsed. The count
    // (not a heuristic) is what tells the importer a product has real variants.
    const productEntries: ScrapedEntry[] = [];

    for (const v of p.variants || []) {
      const ref = v.sku || `${p.handle}-${(p.variants || []).indexOf(v) + 1}`;
      if (!ref) continue;
      const varTitle = v.title === 'Default Title' ? '' : stripInlineMarkup(v.title || '');

      // Parse variant options for movement, size, strap, dial color
      const optVals = [v.option1, v.option2, v.option3].filter(Boolean) as string[];
      let varMovement = '', varMovementType = '', varCaseSize = '', varStrapHint = '', varDialColor = '';
      for (let oi = 0; oi < optVals.length; oi++) {
        const val = optVals[oi];
        const valLow = val.toLowerCase();
        const optName = optionDefs[oi] || '';
        // Dial color is frequently the variant axis (Shopify option "Dial
        // color"). Keep BOTH the canonical color token (resolves against
        // lookup_dial_colors → drives Algolia facets) AND the brand's original
        // name (e.g. "maelstrom blue"), comma-joined, so the variant carries a
        // multi-value dial color and the parent's dialColorVariants unions all
        // of them. The original is dropped only when it equals the canonical.
        if (optName.includes('dial')) {
          const canon = extractDialColorToken(val);
          const original = val.trim().toLowerCase();
          varDialColor = [canon, original]
            .filter(Boolean)
            .filter((x, i, a) => a.indexOf(x) === i)
            .join(', ');
        }
        if (optName.includes('movement') || optName.includes('caliber') || /sellita|miyota|seiko|eta|ronda|soprod|sw[234]\d|nh3\d|vk6/i.test(val)) {
          varMovement = val.trim();
          if (valLow.includes('hand wound') || valLow.includes('hand-wound') || valLow.includes('manual')) varMovementType = 'manual';
          else if (valLow.includes('automatic') || valLow.includes('self-wind')) varMovementType = 'automatic';
          else if (valLow.includes('meca') || /vk6/i.test(val)) varMovementType = 'meca_quartz';
          else if (valLow.includes('quartz') || valLow.includes('solar')) varMovementType = 'quartz';
        }
        if (optName.includes('size') || optName.includes('diameter') || /^\d{2,3}\s*mm$/i.test(val.trim())) {
          const sizeNum = val.match(/(\d{2,3})/);
          if (sizeNum) varCaseSize = sizeNum[1];
        }
        if (optName.includes('strap') || optName.includes('bracelet') || optName.includes('band')
          || valLow.includes('leather') || valLow.includes('rubber') || valLow.includes('bracelet')
          || valLow.includes('cordovan') || valLow.includes('nato')) {
          varStrapHint = val;
        }
      }

      // Strap from variant title + option hints
      let strapMaterial = '', strapColor = '';
      const vLow = (varTitle + ' ' + (varStrapHint || '')).toLowerCase();
      if (vLow.includes('rubber') || vLow.includes('tropic') || vLow.includes('fkm') || vLow.includes('silicone')) strapMaterial = 'rubber';
      else if (vLow.includes('leather') || vLow.includes('calf') || vLow.includes('suede') || vLow.includes('alligator') || vLow.includes('cordovan') || vLow.includes('shell')) strapMaterial = 'leather';
      else if (vLow.includes('milanese') || vLow.includes('mesh')) strapMaterial = 'mesh_bracelet';
      else if (vLow.includes('bracelet') || vLow.includes('steel') || vLow.includes('3-link') || vLow.includes('bor') || vLow.includes('beads')) strapMaterial = 'steel_bracelet';
      else if (vLow.includes('nato') || vLow.includes('nylon') || vLow.includes('canvas')) strapMaterial = 'nato';

      const strapColors = ['black','brown','blue','green','grey','gray','tan','silver','cognac','orange','red'];
      for (const c of strapColors) { if (vLow.includes(c)) { strapColor = c; break; } }

      const imgSrc = imageMap[v.id] || p.image?.src || p.images?.[0]?.src || '';
      // The whole gallery, not just the hero: the variant's own image leads,
      // then the product's images in Shopify's order, each once.
      const galleryUrls = [imgSrc, ...productImageUrls].filter(
        (u, i, a): u is string => !!u && a.indexOf(u) === i,
      );
      const taggedImages: TaggedImage[] = galleryUrls.map((url) => ({ url, tags: [], autoTagged: false }));

      productEntries.push({
        reference: ref,
        model,
        collection,
        productName: productTitle,
        title: varTitle ? `${productTitle} — ${varTitle}` : productTitle,
        description: (specs.description as string) || '',
        variant: varTitle || titleVariant,
        movement: varMovement || (specs.movement as string) || '',
        movementType: varMovementType || (specs.movementType as string) || '',
        jewels: (specs.jewels as string) || '',
        caseSize: varCaseSize || (specs.caseSize as string) || '',
        caseHeight: (specs.caseHeight as string) || '',
        waterRes: (specs.waterRes as string) || '',
        caseMaterial: (specs.caseMaterial as string) || '',
        caseFinish: (specs.caseFinish as string) || '',
        caseCoating: (specs.caseCoating as string) || '',
        dialColor: varDialColor || dialColor,
        hands: (specs.hands as string) || '',
        glass: (specs.glass as string) || '',
        crystal: (specs.crystal as string) || '',
        lugWidth: (specs.lugWidth as string) || '',
        lugToLug: (specs.lugToLug as string) || '',
        powerReserve: (specs.powerReserve as string) || '',
        dialMaterial: (specs.dialMaterial as string) || '',
        crownType: (specs.crownType as string) || '',
        caseBack: (specs.caseBack as string) || '',
        strapMaterial,
        strapColor,
        imgSrc,
        ...(taggedImages.length > 1 ? { taggedImages } : {}),
        url: `${base}/products/${p.handle}`,
        price: v.price,
        tags: tags.join(', '),
        shopifyVendor: typeof p.vendor === 'string' ? p.vendor.trim() : '',
        shopifyProductHandle: p.handle,
        shopifyVariantCount: 0, // stamped below once the product is fully parsed
        shopifyProductId: String(p.id),
        shopifyVariantId: String(v.id),
      });
    }

    // Stamp the final sibling count on every variant of this product, then emit.
    for (const e of productEntries) e.shopifyVariantCount = productEntries.length;
    entries.push(...productEntries);
  }

  // Ensure all entries have reference + model
  entries.forEach((e) => {
    if (!e.reference) e.reference = `unknown-${Math.random().toString(36).slice(2, 8)}`;
    if (!e.model) e.model = e.productName || 'Unknown';
  });

  return entries;
}
